import React from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface ModuleSection {
  title: string;
  content: string[];
  illustrationText?: string;
  additionalSection?: {
    title: string;
    content: string[];
  };
  additionalSection2?: {
    title: string;
    content: string[];
  };
}

const moduleContent: ModuleSection[] = [
  {
    title: 'Balance – Integrating Energy, Movement & Rest',
    content: [
      '<strong>Learning Goal:</strong> Learn how to maintain long-term physical balance by combining activity and rest in harmony.',
      '<br/>',
      '<strong>Key Topics:</strong>',
      '• Energy management throughout the day',
      '• Creating weekly physical balance',
      '• Building sustainable wellness habits',
      '• Integrating movement and rest practices',
      '<br/>',
      '<strong>Discussion:</strong> Wellness is not about extreme efforts or perfect routines–it\'s about finding sustainable balance. When movement and rest work together, you create energy rather than depleting it.'
    ]
  },
  {
    title: 'Energy Management, Not Time Management',
    content: [
      'Physical wellness is about managing your energy cycles, not just scheduling time. Your energy naturally fluctuates throughout the day, and working with these rhythms creates sustainable health.',
      '<br/>',
      '<strong>Understanding Energy Cycles:</strong>',
      '• <strong>Morning (6-12):</strong> Natural energy peak - best for movement and focused work',
      '• <strong>Afternoon (12-6):</strong> Post-lunch dip - time for lighter activity and rest',
      '• <strong>Evening (6-12):</strong> Wind-down phase - focus on recovery and preparation for rest',
      '• <strong>Night (12-6):</strong> Restoration phase - deep sleep and tissue repair',
      '<br/>',
      '<strong>Daily Energy Framework:</strong>',
      '<div class="module-table">' +
        '<div class="table-row header-row">' +
          '<div class="table-header pillar-cell">TIME OF DAY</div>' +
          '<div class="table-header means-cell">BODY FOCUS</div>' +
          '<div class="table-header matters-cell">IDEAL HABIT</div>' +
        '</div>' +
        '<div class="table-row">' +
          '<div class="table-cell pillar-cell">Morning</div>' +
          '<div class="table-cell means-cell">Energy building</div>' +
          '<div class="table-cell matters-cell">Stretch, hydrate, short walk</div>' +
        '</div>' +
        '<div class="table-row">' +
          '<div class="table-cell pillar-cell">Midday</div>' +
          '<div class="table-cell means-cell">Productivity zone</div>' +
          '<div class="table-cell matters-cell">Active breaks, light lunch</div>' +
        '</div>' +
        '<div class="table-row">' +
          '<div class="table-cell pillar-cell">Evening</div>' +
          '<div class="table-cell means-cell">Recovery phase</div>' +
          '<div class="table-cell matters-cell">Wind down, reflect, relax</div>' +
        '</div>' +
      '</div>',
      '<br/>',
      '<strong>Key Insight:</strong> Working against your natural energy cycles creates fatigue. Working with them builds sustainable vitality.'
    ]
  },
  {
    title: 'Weekly Physical Balance Plan',
    content: [
      'Create a balanced weekly routine that includes movement, rest, and recovery. The goal is consistency over perfection.',
      '<br/>',
      '<strong>Essential Balance Areas:</strong>',
      '<div class="module-table">' +
        '<div class="table-row header-row">' +
          '<div class="table-header">Area</div>' +
          '<div class="table-header">Ideal Goal</div>' +
          '<div class="table-header">Your Plan</div>' +
        '</div>' +
        '<div class="table-row">' +
          '<div class="table-cell pillar-cell">Movement</div>' +
          '<div class="table-cell means-cell">150+ minutes of moderate activity/week</div>' +
          '<div class="table-cell matters-cell">☑ Yes ☑ No</div>' +
        '</div>' +
        '<div class="table-row">' +
          '<div class="table-cell pillar-cell">Strength</div>' +
          '<div class="table-cell means-cell">2× weekly strength or core sessions</div>' +
          '<div class="table-cell matters-cell">☑ Yes ☑ No</div>' +
        '</div>' +
        '<div class="table-row">' +
          '<div class="table-cell pillar-cell">Flexibility</div>' +
          '<div class="table-cell means-cell">10 mins of stretching daily</div>' +
          '<div class="table-cell matters-cell">☑ Yes ☑ No</div>' +
        '</div>' +
        '<div class="table-row">' +
          '<div class="table-cell pillar-cell">Sleep</div>' +
          '<div class="table-cell means-cell">7–9 hrs/night</div>' +
          '<div class="table-cell matters-cell">☑ Yes ☑ No</div>' +
        '</div>' +
        '<div class="table-row">' +
          '<div class="table-cell pillar-cell">Sitting Time</div>' +
          '<div class="table-cell means-cell">&lt;6 hrs/day</div>' +
          '<div class="table-cell matters-cell">☑ Yes ☑ No</div>' +
        '</div>' +
      '</div>',
      '<br/>',
      '<strong>Building Your Weekly Plan:</strong>',
      '• <strong>Monday-Friday:</strong> Focus on consistency with work-friendly activities',
      '• <strong>Weekends:</strong> Include more restorative or recreational movement',
      '• <strong>Flexibility:</strong> Adjust based on how you feel and your schedule',
      '• <strong>Progress Tracking:</strong> Mark what you accomplish each week'
    ],
    additionalSection: {
      title: 'Weekly Planning Activity',
      content: [
        'Design your personal weekly balance plan:',
        '• <strong>Movement:</strong> What 2-3 activities will you do this week?',
        '• <strong>Rest:</strong> What time will you aim for bed/wake up?',
        '• <strong>Recovery:</strong> When will you include stretching or relaxation?',
        '• <strong>Flexibility:</strong> What\'s your backup plan if something comes up?',
        '<br/>',
        '<strong>Remember:</strong> Start with what feels manageable. You can always add more as you build the habit.'
      ]
    }
  },
  {
    title: 'Reflection Exercise',
    content: [
      'Take time to assess your current physical balance and identify areas for improvement.',
      '<br/>',
      '<strong>Energy Awareness Questions:</strong>',
      '• When do you feel most energetic during the day?',
      '• When do you feel drained or sleepy?',
      '• How does your current schedule align with your energy patterns?',
      '• What activities give you energy vs. deplete it?',
      '<br/>',
      '<strong>Balance Assessment:</strong>',
      '• <strong>Movement:</strong> Are you getting enough physical activity?',
      '• <strong>Rest:</strong> Is your sleep quality and quantity adequate?',
      '• <strong>Recovery:</strong> Do you have time for relaxation and stress relief?',
      '• <strong>Nutrition:</strong> How does your eating support your energy levels?',
      '<br/>',
      '<strong>Common Imbalance Signs:</strong>',
      '• Constant fatigue despite adequate sleep',
      '• Difficulty concentrating or staying motivated',
      '• Mood swings or irritability',
      '• Physical tension or discomfort',
      '• Reduced immunity or frequent illness',
      '<br/>',
      '<strong>Tip:</strong> You don\'t have to change everything at once. Start with one area that would make the biggest difference in your daily energy.'
    ],
    additionalSection: {
      title: 'Energy Pattern Journal',
      content: [
        'Track your energy for 3 days:',
        '• <strong>Morning (upon waking):</strong> Energy level 1-10',
        '• <strong>Mid-morning (9-11 AM):</strong> Energy level 1-10',
        '• <strong>Afternoon (12-5 PM):</strong> Energy level 1-10',
        '• <strong>Evening (6-10 PM):</strong> Energy level 1-10',
        '• <strong>Factors:</strong> What affected your energy (food, activity, stress)?',
        '<br/>',
        '<strong>Pattern Recognition:</strong> When are you most productive? When do you need rest? How can you adjust your schedule to match these patterns?'
      ]
    }
  },
  {
    title: 'Mini Action Plan',
    content: [
      'Create specific, achievable steps to improve your physical balance. Focus on small changes that compound over time.',
      '<br/>',
      '<div class="table-scroll-container">' +
        '<div class="module-table">' +
          '<div class="table-row header-row">' +
            '<div class="table-header pillar-cell left-align">Goal</div>' +
            '<div class="table-header means-cell left-align">Action Step</div>' +
            '<div class="table-header matters-cell left-align">Timeframe</div>' +
          '</div>' +
          '<div class="table-row">' +
            '<div class="table-cell pillar-cell left-align">Move More</div>' +
            '<div class="table-cell means-cell left-align">Add 15 minutes of walking daily</div>' +
            '<div class="table-cell matters-cell left-align">1 week</div>' +
          '</div>' +
          '<div class="table-row">' +
            '<div class="table-cell pillar-cell left-align">Sleep Better</div>' +
            '<div class="table-cell means-cell left-align">No phone after 10 PM</div>' +
            '<div class="table-cell matters-cell left-align">3 nights a week</div>' +
          '</div>' +
          '<div class="table-row">' +
            '<div class="table-cell pillar-cell left-align">Sit Less</div>' +
            '<div class="table-cell means-cell left-align">Stand/stretch every hour</div>' +
            '<div class="table-cell matters-cell left-align">Daily</div>' +
          '</div>' +
        '</div>' +
      '</div>',
      '<br/>',
      '<strong>Implementation Tips:</strong>',
      '• <strong>Start Small:</strong> Choose changes you can maintain consistently',
      '• <strong>Track Progress:</strong> Use a habit app or simple checklist',
      '• <strong>Be Kind:</strong> If you miss a day, restart the next day without judgment',
      '• <strong>Celebrate Wins:</strong> Notice and appreciate your progress',
      '• <strong>Adjust as Needed:</strong> If something doesn\'t work, modify your approach'
    ],
  },
    {
      title: 'Key Takeaways',
      content: [
        'Movement fuels your energy.',
        'Sleep restores your strength.',
        'Balance prevents burnout.',
        '<br/>',
        '<strong>Small steps done consistently become lifelong wellness habits.</strong>',
        '<strong>Remember:</strong> Physical wellness is a journey, not a destination. Focus on progress over perfection, and celebrate the ways your body becomes stronger, more resilient, and better able to support your life.'
      ]
    },
  {
    title: 'Ready to Test Your Knowledge?',
    content: [
      'You\'ve completed Module 3: Balance – Integrating Energy, Movement & Rest! Now, let\'s reinforce your learning with a quick quiz.'
    ],
    additionalSection: {
      title: 'Next Steps',
      content: [
        'Click "Take the Quiz" below to test your knowledge about physical balance and energy management.',
        'The quiz will help you review key concepts about integrating movement and rest for sustainable wellness.'
      ]
    }
  }
];

const PhysicalModule3Page: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress, isCompleted } = useModuleProgress('physicalM3');
  const totalSteps = moduleContent.length;

  const safeCurrentStep = Math.max(0, Math.min(currentStep, totalSteps - 1));
  const currentModule = moduleContent[safeCurrentStep];
  const progress = ((safeCurrentStep + 1) / totalSteps) * 100;

  const handleNext = async () => {
    if (currentStep < totalSteps - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      await saveProgress(nextStep, false);
    } else {
      await saveProgress(currentStep, true);
      navigate('/panel/learn/physical-module-3/quiz');
    }
  };

  const handleBack = async () => {
    if (currentStep === 0) {
      navigate('/panel/learn/physical-resources');
      return;
    }
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    await saveProgress(prevStep, isCompleted);
  };

  const showCompletionMessage = currentStep === totalSteps - 1;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  if (!currentModule) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Error loading module content. Please try refreshing the page.</p>
      </div>
    );
  }

  return (
    <div className="module-form">
      <div className="module-container">
        <div
          className="module-left physical-panel"
        >
          <div className="module-header">
            <h1>Module 3</h1>
            <p className="module-subtitle">Balance – Integrating Energy, Movement & Rest</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Learn how to maintain long-term physical balance by combining activity and rest in harmony.
            </p>
          </div>
        </div>

        <div className="module-right">
          <div className="module-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Page {currentStep + 1} of {totalSteps}
              </span>
            </div>

            <div className="module-section">
              <h2 className="module-title">
                {currentModule.title}
              </h2>
              <div className="content-section">
                {currentModule.content.map((paragraph, index) => (
                  <p key={index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
              </div>
            </div>

            {currentModule.additionalSection && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection.content.map((paragraph: string, index: number) => (
                    <p key={`additional-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
            {currentModule.additionalSection2 && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection2.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection2.content.map((paragraph: string, index: number) => (
                    <p key={`additional2-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={handleBack}
              disabled={false}
            >
              {currentStep === 0 ? 'Exit Module' : 'Previous'}
            </button>

            {showCompletionMessage ? (
              <button
                className="btn-primary"
                onClick={() => navigate('/panel/learn/physical-module-3/quiz')}
              >
                Take the Quiz
              </button>
            ) : (
              <button
                className="btn-primary"
                onClick={handleNext}
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhysicalModule3Page;

